import type { components } from '@nutians/api-client';
/**
 * LogHistoryPanel — shows today's submitted log groups in reverse-submission
 * order, each with its decrypted original text and entry chips.
 *
 * Two modes per group:
 *  1. Read-only: "You logged: <text>" + EntryChip list
 *  2. Edit & re-log: text textarea pre-filled, submit soft-deletes old entries
 *     and re-submits through POST /me/logs. The full re-log flow preserves the
 *     same LogPanel state (onNewLog callback) so callers stay in sync.
 *
 * Synthetic groups (text === null, e.g. barcode/manual entries) show only the
 * chips — no "You logged:" headline and no edit affordance.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import { DAY_ENTRIES_KEY } from '../hooks/useDayEntries.js';
import { EntryChip } from './EntryChip.js';

type EntryListGroup = components['schemas']['EntryListGroup'];
type DaySummary = components['schemas']['DaySummary'];
type LogResponse = components['schemas']['LogResponse'];
type MealEntryView = components['schemas']['MealEntryView'];

interface LogHistoryPanelProps {
  groups: EntryListGroup[];
  date: string;
  onDayUpdate: (day: DaySummary) => void;
  /** Called when an "Edit & re-log" re-submission produces a new log result. */
  onNewLog?: (result: LogResponse) => void;
}

// ── Single group card ─────────────────────────────────────────────────────────

interface GroupCardProps {
  group: EntryListGroup;
  date: string;
  onDayUpdate: (day: DaySummary) => void;
  onNewLog?: (result: LogResponse) => void;
}

function GroupCard({ group, date, onDayUpdate, onNewLog }: GroupCardProps) {
  const { t } = useTranslation('log');
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(group.text ?? '');

  const relogMutation = useMutation({
    mutationFn: async (newText: string) => {
      // Soft-delete all non-deleted entries in this group first.
      const activeEntries = group.entries.filter((e) => e.deletedAt === null);
      await Promise.all(
        activeEntries.map((e) =>
          api.DELETE('/me/entries/{id}', { params: { path: { id: e.id } } }),
        ),
      );

      // Re-submit the edited text through the same pipeline.
      const { data, error } = await api.POST('/me/logs', {
        body: {
          text: newText,
          clientNow: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      if (error || !data) throw new Error('Re-log failed');
      return data;
    },
    onSuccess: (data) => {
      onDayUpdate(data.day);
      void qc.invalidateQueries({ queryKey: DAY_ENTRIES_KEY(date) });
      setEditing(false);
      onNewLog?.(data);
    },
  });

  const handleRelogSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = editText.trim();
    if (!trimmed || relogMutation.isPending) return;
    relogMutation.mutate(trimmed);
  };

  const handleDeleteRequest = (_deleted: MealEntryView) => {
    void qc.invalidateQueries({ queryKey: DAY_ENTRIES_KEY(date) });
  };

  const hasText = group.text !== null;
  const activeEntries = group.entries.filter((e) => e.deletedAt === null);

  if (activeEntries.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ink/10 bg-shell/50 px-3 py-3 dark:border-ink/10 dark:bg-shell/20">
      {hasText && !editing && (
        <div className="flex items-start justify-between gap-2">
          <p
            className="flex-1 text-xs text-ink-soft dark:text-ink-soft line-clamp-2"
            title={group.text ?? undefined}
          >
            {t('history.youLogged', { text: group.text })}
          </p>
          <button
            type="button"
            onClick={() => {
              setEditText(group.text ?? '');
              setEditing(true);
            }}
            className="shrink-0 text-xs text-leaf hover:underline focus:outline-none focus:underline"
          >
            {t('history.edit')}
          </button>
        </div>
      )}

      {editing && (
        <form onSubmit={handleRelogSubmit} className="flex flex-col gap-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={2}
            disabled={relogMutation.isPending}
            className="w-full resize-none rounded border border-ink/20 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-leaf disabled:opacity-50
                       dark:border-ink/20 dark:bg-shell dark:text-ink"
            aria-label={t('label')}
          />
          {relogMutation.isError && (
            <p role="alert" className="text-xs text-clay dark:text-clay">
              {t('history.relogError')}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={relogMutation.isPending || editText.trim().length === 0}
              className="rounded px-3 py-1.5 text-xs font-medium bg-leaf text-white
                         hover:bg-leaf-deep disabled:opacity-50
                         focus:outline-none focus:ring-2 focus:ring-leaf min-h-[44px] min-w-[44px]"
            >
              {relogMutation.isPending ? t('history.relogging') : t('history.editSubmit')}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={relogMutation.isPending}
              className="rounded px-3 py-1.5 text-xs font-medium text-ink-soft
                         hover:text-ink disabled:opacity-50
                         focus:outline-none focus:ring-2 focus:ring-leaf min-h-[44px] min-w-[44px]"
            >
              {t('history.editCancel')}
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-col gap-1.5">
        {activeEntries.map((entry) => (
          <EntryChip
            key={entry.id}
            entry={entry}
            onDayUpdate={onDayUpdate}
            onDeleteRequest={handleDeleteRequest}
          />
        ))}
      </div>
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function LogHistoryPanel({ groups, date, onDayUpdate, onNewLog }: LogHistoryPanelProps) {
  const { t } = useTranslation('log');

  const visibleGroups = groups.filter((g) => g.entries.some((e) => e.deletedAt === null));
  if (visibleGroups.length === 0) return null;

  return (
    <section aria-label={t('history.heading')} className="flex flex-col gap-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
        {t('history.heading')}
      </h3>
      {visibleGroups.map((group) => (
        <GroupCard
          key={group.rawLogId}
          group={group}
          date={date}
          onDayUpdate={onDayUpdate}
          onNewLog={onNewLog}
        />
      ))}
    </section>
  );
}
